import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import * as Highcharts from 'highcharts';

export interface ThresholdChartData {
  threshold: number;
  adoptionRate: number;
  RmseChange: number;
  avgPerformanceScore: number;
}

@Component({
  selector: 'threshold-chart',
  templateUrl: './threshold-chart.component.html',
  styleUrls: ['./threshold-chart.component.scss'],
  standalone: false
})
export class ThresholdChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('sliderRef', { static: true }) sliderRef!: ElementRef;
  @Input() chartWidth = 'chart-small';
  ngAfterViewInit(): void {
    window.addEventListener('scroll', this.handleScroll, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  handleScroll = () => {
    // Force a resize event to trigger tooltip reposition
    window.dispatchEvent(new Event('resize'));
  };
  @Input() data: ThresholdChartData[] = [];

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  // Slider configuration
  sliderValue = 0.7;
  sliderMin = 0;
  sliderMax = 1.0;
  sliderStep = 0.05;

  // Current metrics
  currentAdoptionRate = 0;
  currentAvgPerformanceScore = 0;
  currentRmseChange = 0;

  ngOnInit(): void {
    this.initializeChart();
    this.updateMetrics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.initializeChart();
      this.updateMetrics();
    }
  }

  onSliderChange(value: number): void {
    this.sliderValue = value;
    this.updateChart();
    this.updateMetrics();
  }

  private initializeChart(): void {
    if (!this.data || this.data.length === 0) {
      return;
    }

    const thresholds = this.data.map(d => d.threshold);
    const adoptionRates = this.data.map(d => d.adoptionRate);
    const accuracies = this.data.map(d => d.RmseChange);

    this.chartOptions = {
      chart: {
        type: 'line',
        height: 400,
        backgroundColor: '#ffffff'
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories: thresholds.map(t => t.toFixed(2)),
        title: {
          text: 'ML Performance Score Threshold',
          style: {
            color: '#29333d',
            fontSize: '14px',
            fontWeight: '600'
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#e0e5eb',
        lineColor: '#e0e5eb',
        tickColor: '#e0e5eb'
      },
      yAxis: [
        {
          // Left axis - Adoption Rate
          title: {
            text: 'Adoption Rate (%)',
            style: {
              color: '#29333d',
              fontSize: '14px',
              fontWeight: '600'
            }
          },
          labels: {
            format: '{value}%',
            style: {
              color: '#29333d'
            }
          },
          min: 0,
          max: 100,
          gridLineColor: '#e0e5eb',
          gridLineWidth: 1,
          // Force top grid line
          tickPositions: [0, 20, 40, 60, 80, 100]
        },
        {
          // Right axis - RmseChange
          title: {
            text: 'RMSE Change',
            style: {
              color: '#92ad38',
              fontSize: '14px',
              fontWeight: '600'
            }
          },
          labels: {
            format: '{value}',
            style: {
              color: '#92ad38'
            }
          },
          min: 0,
          max: 100,
          opposite: true,
          gridLineWidth: 0,
          tickPositions: [0, 20, 40, 60, 80, 100]
        }
      ],
      plotOptions: {
        line: {
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        },
        series: {
          states: {
            hover: {
              lineWidthPlus: 1
            }
          }
        }
      },
      tooltip: {
        shared: true,
        backgroundColor: '#ffffff',
        borderColor: '#e0e5eb',
        borderRadius: 8,
        shadow: false,
        useHTML: true,
        formatter: function () {
          const threshold = this.x;
          let tooltip = `<div style="padding: 8px;">`;
          tooltip += `<div style="font-weight: 700; margin-bottom: 8px;">Threshold: ${threshold}</div>`;

          this.points?.forEach((point: any) => {
            const color = point.series.color;
            const name = point.series.name;
            const value = point.y.toFixed(1);
            tooltip += `<div style="margin: 4px 0;">`;
            tooltip += `<span style="color: ${color};">●</span> `;
            tooltip += `${name}: <strong>${value}%</strong>`;
            tooltip += `</div>`;
          });

          tooltip += `</div>`;
          return tooltip;
        }
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '12px',
          fontWeight: '600',
          color: '#29333d'
        },
        itemMarginTop: 5,
        itemMarginBottom: 5,
        y: -10
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      series: [
        {
          name: 'Adoption Rate (%)',
          type: 'line',
          data: adoptionRates,
          color: '#29333d',
          lineWidth: 2,
          yAxis: 0
        },
        {
          name: 'Estimated RMSE Change',
          type: 'line',
          data: accuracies,
          color: '#92ad38',
          lineWidth: 2,
          yAxis: 1
        },
        {
          // Vertical line for current threshold
          name: 'Current Threshold',
          type: 'line',
          data: [],
          color: '#930011',
          lineWidth: 2,
          dashStyle: 'Dot',
          marker: {
            enabled: false
          },
          enableMouseTracking: false,
          showInLegend: false
        }
      ]
    };

    this.updateChart();
  }

  private updateChart(): void {
    if (!this.chartOptions.series || !this.data || this.data.length === 0) {
      return;
    }

    // Find the index of the current threshold
    const thresholdIndex = this.data.findIndex(d => Math.abs(d.threshold - this.sliderValue) < 0.001);

    if (thresholdIndex >= 0) {
      // Update the vertical line series
      const lineData: [number, number | null][] = this.data.map((_, index) => {
        if (index === thresholdIndex) {
          return [index, 100];
        }
        return [index, null];
      });

      const verticalLineSeries = this.chartOptions.series[2] as Highcharts.SeriesLineOptions;
      if (verticalLineSeries) {
        verticalLineSeries.data = lineData.map((point, index) => {
          if (point[1] === null) {
            return null;
          }
          return {
            x: index,
            y: 0,
            marker: { enabled: false },
            dataLabels: { enabled: false }
          };
        });

        // Create plot line instead for vertical line
        this.chartOptions.xAxis = {
          ...(this.chartOptions.xAxis as Highcharts.XAxisOptions),
          plotLines: [
            {
              color: '#930011',
              width: 2,
              value: thresholdIndex,
              dashStyle: 'Dot',
              zIndex: 5
            }
          ]
        };
      }
    }

    // Force chart update
    this.chartOptions = { ...this.chartOptions };
  }

  public updateMetrics(): void {
    if (!this.data || this.data.length === 0) {
      return;
    }

    // Find closest threshold data point
    const closestData = this.data.reduce((prev, curr) => {
      return Math.abs(curr.threshold - this.sliderValue) < Math.abs(prev.threshold - this.sliderValue) ? curr : prev;
    });

    this.currentAdoptionRate = closestData.adoptionRate;
    this.currentAvgPerformanceScore = closestData.avgPerformanceScore;
    this.currentRmseChange = closestData.RmseChange;
  }

  formatSliderTooltip = (value: number): string => {
    return value.toFixed(2);
  };
}
